import { DataUtil } from "./_DataUtil";
import { getUniqueIdWithPrefix } from "./_TypesHelpers";

export interface EventMeta {
  name: string;
  callback: Function;
  one: boolean;
  fired: boolean;
}

export class EventHandlerUtil {
  static store: {
    [name: string]: {
      [handlerId: string]: EventMeta;
    };
  } = {};

  private static setEventMetaByNameAndHandlerId(
    name: string,
    handlerId: string,
    meta: EventMeta,
  ): void {
    const handlers = (EventHandlerUtil.store[name] ??= {});
    handlers[handlerId] = meta;
  }

  private static addEvent(
    element: HTMLElement,
    name: string,
    callback: Function,
    one: boolean = false,
  ): string {
    const handlerId = getUniqueIdWithPrefix("event");
    const data = DataUtil.get(element, name);
    const handlersIds = data ? (data as string[]) : [];
    handlersIds.push(handlerId);

    DataUtil.set(element, name, handlersIds);

    const meta: EventMeta = {
      name: name,
      callback: callback,
      one: one,
      fired: false,
    };

    EventHandlerUtil.setEventMetaByNameAndHandlerId(name, handlerId, meta);
    return handlerId;
  }

  private static removeEvent(
    element: HTMLElement,
    name: string,
    handerId: string,
  ) {
    DataUtil.removeOne(element, name, handerId);
    // Upstream Metronic had this test inverted (`if (handlersIds) return`): it
    // returned whenever handlers existed, so the store entry was never deleted,
    // and when none existed it read a property of `undefined` and threw.
    // Found by noUncheckedIndexedAccess.
    const handlersIds = EventHandlerUtil.store[name];
    if (!handlersIds) {
      return;
    }

    delete handlersIds[handerId];
  }

  public static trigger(
    element: HTMLElement,
    name: string,
    target?: any,
    _e?: Event,
  ): boolean {
    let returnValue = true;
    if (!DataUtil.has(element, name)) {
      return returnValue;
    }

    let eventValue;
    let handlerId;
    const data = DataUtil.get(element, name);
    const handlersIds = data ? (data as string[]) : [];
    for (handlerId of handlersIds) {
      const handler = EventHandlerUtil.store[name]?.[handlerId];
      if (handler) {
        if (handler.name === name) {
          if (handler.one) {
            if (handler.fired) {
              handler.fired = true;
              eventValue = handler.callback.call(this, target);
            }
          } else {
            eventValue = handler.callback.call(this, target);
          }

          if (eventValue === false) {
            returnValue = false;
          }
        }
      }
    }
    return returnValue;
  }

  public static on = function (
    element: HTMLElement,
    name: string,
    callBack: Function,
  ): void {
    EventHandlerUtil.addEvent(element, name, callBack, false);
  };

  public static one(
    element: HTMLElement,
    name: string,
    callBack: Function,
  ): void {
    EventHandlerUtil.addEvent(element, name, callBack, true);
  }

  public static off(
    element: HTMLElement,
    name: string,
    handerId: string,
  ): void {
    EventHandlerUtil.removeEvent(element, name, handerId);
  }
}
